import { useEffect, useRef, useState } from 'react'
import type mapboxgl from 'mapbox-gl'
import { MAPBOX_TOKEN } from '../lib/geocode'
import { reportMapWebglFallback } from '../lib/mapWebgl'

interface ChurchLocationMapProps {
  lat: number
  lng: number
  label?: string
  height?: number
}

export function ChurchLocationMap({ lat, lng, label, height = 160 }: ChurchLocationMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const markerRef = useRef<mapboxgl.Marker | null>(null)
  // Runtime WebGL init failure → fall back to the OSM embed below.
  const [webglFailed, setWebglFailed] = useState(false)
  // Mapbox GL needs WebGL — skip in Vitest/jsdom; use OSM embed fallback there.
  const useMapbox = Boolean(MAPBOX_TOKEN) && import.meta.env.MODE !== 'test' && !webglFailed

  useEffect(() => {
    if (!containerRef.current || !useMapbox) return
    let cancelled = false

    // Any throw here (most commonly `Failed to initialize WebGL` from `new mb.Map`)
    // must be caught, not left as an unhandled rejection — flip to the OSM fallback.
    const handleInitError = (err: unknown) => {
      if (cancelled) return
      reportMapWebglFallback(err)
      setWebglFailed(true)
    }

    void import('mapbox-gl').then((mod) => {
      if (cancelled || !containerRef.current) return
      try {
        const mb = mod.default
        mb.accessToken = MAPBOX_TOKEN as string

        const map = new mb.Map({
          container: containerRef.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [lng, lat],
          zoom: 14,
          interactive: false,
          attributionControl: true,
        })

        mapRef.current = map

        map.on('load', () => {
          if (cancelled) return
          map.resize()
          markerRef.current = new mb.Marker({ color: '#3B4CCA' })
            .setLngLat([lng, lat])
            .addTo(map)
        })
      } catch (err) {
        handleInitError(err)
      }
    }).catch(handleInitError)

    return () => {
      cancelled = true
      markerRef.current?.remove()
      markerRef.current = null
      mapRef.current?.remove()
      mapRef.current = null
    }
  }, [useMapbox, lat, lng])

  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return
    mapRef.current.setCenter([lng, lat])
    markerRef.current.setLngLat([lng, lat])
  }, [lat, lng])

  if (!useMapbox) {
    const pad = 0.012
    const bbox = `${lng - pad},${lat - pad},${lng + pad},${lat + pad}`
    const embedSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${lat}%2C${lng}`

    return (
      <iframe
        title={label ? `Map showing ${label}` : 'Church location map'}
        src={embedSrc}
        style={{
          height,
          width: '100%',
          border: 0,
          borderRadius: 10,
          display: 'block',
        }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    )
  }

  return (
    <div
      ref={containerRef}
      style={{ height, borderRadius: 10, overflow: 'hidden' }}
      role="img"
      aria-label={label ? `Map showing ${label}` : 'Church location map'}
    />
  )
}
